import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { PageHeader } from "@/components/PageHeader";
import { api, errMsg } from "@/lib/api";

const DAYS = [["mon", "Monday"], ["tue", "Tuesday"], ["wed", "Wednesday"], ["thu", "Thursday"], ["fri", "Friday"], ["sat", "Saturday"], ["sun", "Sunday"]];
const DEFAULT = { available: true, start: "00:00", end: "23:59", note: "" };

const ReminderPref = () => {
  const [pref, setPref] = useState(null);
  useEffect(() => {
    api.get("/me/preferences").then((r) => setPref(r.data));
  }, []);
  if (!pref) return null;
  const toggle = async (v) => {
    try {
      await api.put("/me/preferences", { shift_reminders: v });
      setPref({ ...pref, shift_reminders: v });
      toast.success(v ? "Shift reminders on" : "Shift reminders off");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="mb-6 rounded-xl border border-slate-200 bg-white p-4" data-testid="reminder-pref-card">
      <label className="flex items-center justify-between gap-3 text-sm font-semibold">
        Email me a reminder the evening before my shifts
        <Switch checked={pref.shift_reminders} onCheckedChange={toggle} data-testid="reminder-pref-switch" />
      </label>
      <p className="mt-1 text-xs text-slate-500" data-testid="reminder-pref-note">
        {pref.business_mode === "off" ? "Your workplace hasn't turned shift reminders on yet." : "You'll still get rota, change and request emails."}
      </p>
    </div>
  );
};

export default function Availability() {
  const [a, setA] = useState(null);
  useEffect(() => {
    api.get("/me/availability").then((r) => setA(Object.fromEntries(DAYS.map(([k]) => [k, { ...DEFAULT, ...(r.data[k] || {}) }]))));
  }, []);
  if (!a) return null;
  const set = (k, patch) => setA({ ...a, [k]: { ...a[k], ...patch } });

  const save = async () => {
    try {
      await api.put("/me/availability", { availability: a });
      toast.success("Availability saved");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="mx-auto max-w-2xl">
      <PageHeader title="Availability" subtitle="Let your manager know when you can work" />
      <ReminderPref />
      <div className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
        {DAYS.map(([k, label]) => (
          <div key={k} className="flex flex-wrap items-center gap-3 p-4" data-testid={`avail-row-${k}`}>
            <Switch checked={a[k].available} onCheckedChange={(v) => set(k, { available: v })} data-testid={`avail-switch-${k}`} />
            <span className="w-28 font-semibold">{label}</span>
            {a[k].available ? (
              <div className="flex items-center gap-2">
                <Input type="time" className="w-28" value={a[k].start} onChange={(e) => set(k, { start: e.target.value })} data-testid={`avail-start-${k}`} />
                <span className="text-slate-400">to</span>
                <Input type="time" className="w-28" value={a[k].end} onChange={(e) => set(k, { end: e.target.value })} data-testid={`avail-end-${k}`} />
              </div>
            ) : <span className="text-sm text-rose-600">Unavailable</span>}
          </div>
        ))}
      </div>
      <Button onClick={save} className="mt-6 w-full rounded-full" data-testid="avail-save-btn">Save availability</Button>
    </div>
  );
}
